// app/javascript/controllers/rsvp_payment_form_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "form", "amountInput", "otherWrapper",
        "giftAmt", "charityAmt", "receiptNote", "receiptAmt",
        "taxToggle", "taxBlock",
        "payBlock", "amountBlock", "identityBlock", "submit",
        "feeCopy", "totalCopy", "chips",
        "giftChoiceCard", "giftChoiceIndicator",
        "paymentChoiceCard", "afterAmountBlock", "splitPreview", "taxReceiptChoice", "fallbackAmount"
    ]

    static values = {
        hostSplit: Number,
        charityName: String,
        taxThreshold: Number,
        currencySymbol: String,
        feeLowThreshold: Number,
        feeMidUpper: Number,
        feeRateBps: Number,
        feeMidFlatCents: Number,
        charityRate: Number,
        intent: String
    }

    connect() {
        this.selectedAmount = parseInt(this.amountInputTarget.value || "0", 10) || 0
        const other = this.otherWrapperTarget.querySelector('[name="other_amount"]')
        this.usingOther = !!other?.value
        this.amountTouched = false

        this.toggleGift({ fromInit: true })
        this.onPaymethodChange()
        this.onTaxToggle()

        if (this.hasChipsTarget) {
            if (this.usingOther) {
                this.highlightOtherChip()
                this.otherWrapperTarget.classList.remove("d-none")
            } else if (this.selectedAmount > 0) {
                this.highlightChip(this.selectedAmount)
            }
            this.chipsTarget.classList.remove("d-none")
        }
        // Keep a usable native form until enhancement has successfully connected.
        this.amountInputTarget.type = "hidden"
        if (this.hasFallbackAmountTarget) this.fallbackAmountTarget.classList.add("d-none")
        this.element.classList.add("rsvp-enhanced")
    }

    isAttend() {
        return (this.intentValue || "").toLowerCase() === "attend"
    }

    isGivingGift() {
        const radio = this.formTarget.querySelector('input[name="gift"]:checked')
        return radio && radio.value === "yes"
    }

    isIdentityVisible() {
        return this.hasIdentityBlockTarget && !this.identityBlockTarget.classList.contains("d-none")
    }

    valuePresent(name) {
        const el = this.formTarget.querySelector(`[name="${name}"]`)
        return el && String(el.value || "").trim().length > 0
    }

    validateBeforeSubmit(event) {
        const valid = this.validateAmountSelection({ showError: true })

        if (!valid) {
            event.preventDefault()
            event.stopImmediatePropagation()

            window.setTimeout(() => {
                this.validateAmountSelection({ showError: true })
                this.focusAmountErrorArea()
            }, 0)

            return false
        }
    }

    validateAmountSelection({ showError = false, deferError = false } = {}) {
        if (!this.isGivingGift()) {
            this.clearAmountError()
            return true
        }

        if (this.usingOther) {
            const input = this.otherWrapperTarget?.querySelector('[name="other_amount"]')
            const rawValue = input ? String(input.value || "").trim() : ""
            const value = parseInt(rawValue || "0", 10) || 0

            if (rawValue.length === 0) {
                if (showError) {
                    if (deferError) {
                        this.showAmountErrorAfterValidation("Please select or enter an amount (minimum $10)")
                    } else {
                        this.showAmountError("Please select or enter an amount (minimum $10)")
                    }
                }
                return false
            }

            if (value < 10) {
                if (showError) {
                    if (deferError) {
                        this.showAmountErrorAfterValidation("Please enter an amount of at least $10.")
                    } else {
                        this.showAmountError("Please enter an amount of at least $10.")
                    }
                }
                return false
            }

            this.clearAmountError()
            return true
        }

        const amount = parseInt(this.amountInputTarget.value || "0", 10) || 0

        if (amount <= 0) {
            if (showError) {
                if (deferError) {
                    this.showAmountErrorAfterValidation("Please select or enter an amount (minimum $10)")
                } else {
                    this.showAmountError("Please select or enter an amount (minimum $10)")
                }
            }
            return false
        }

        this.clearAmountError()
        return true
    }

    showAmountError(message) {
        const error = this.amountErrorElement()
        if (!error) return

        error.textContent = message
        error.style.removeProperty("display")
        error.style.display = "block"
    }

    showAmountErrorAfterValidation(message) {
        window.setTimeout(() => {
            this.showAmountError(message)
        }, 0)
    }

    clearAmountError() {
        const error = this.amountErrorElement()
        if (!error) return

        error.textContent = ""
        error.style.display = "none"
    }

    amountErrorElement() {
        return this.formTarget.querySelector("#amount-error")
    }

    focusAmountErrorArea() {
        if (this.usingOther && this.hasOtherWrapperTarget) {
            const input = this.otherWrapperTarget.querySelector('[name="other_amount"]')
            input?.focus()
            return
        }

        if (this.hasChipsTarget) {
            this.chipsTarget.scrollIntoView({ behavior: "smooth", block: "center" })
        }
    }

    onPaymethodChange() {
        this.syncPaymentChoiceCards()
    }

    syncPaymentChoiceCards() {
        if (!this.hasPaymentChoiceCardTarget) return

        const selectedValue = this.formTarget.querySelector('input[name="paymethod"]:checked')?.value || "card"

        this.paymentChoiceCardTargets.forEach((card) => {
            const active = card.dataset.paymentValue === selectedValue
            card.classList.toggle("rsvp-contribution-v2__payment-card--active", active)
        })
    }

    onTaxToggle() {
        if (!this.hasTaxToggleTarget || !this.hasTaxBlockTarget) return

        const show = this.taxToggleTarget.checked
        this.taxBlockTarget.classList.toggle("d-none", !show)
    }

    toggleGift({ fromInit = false } = {}) {
        const give = this.isGivingGift()

        this.syncGiftChoiceCards()

        if (this.hasAmountBlockTarget) {
            this.amountBlockTarget.classList.toggle("d-none", !give)
        }

        if (!give) {
            this.resetAmountSelection()
            this.clearAmountError()
            this.updateAll(0)
            this.amountTouched = false
            this.updateTaxToggleState({ eligible: false })
            this.formTarget?.dispatchEvent(new CustomEvent("rsvp:amount-changed", { bubbles: true }))
            return
        }

        const amt = parseInt(this.amountInputTarget.value || "0", 10) || 0
        this.updateAll(amt)
    }

    syncGiftChoiceCards() {
        if (!this.hasGiftChoiceCardTarget) return

        const selectedValue = this.formTarget.querySelector('input[name="gift"]:checked')?.value || "yes"

        this.giftChoiceCardTargets.forEach((card) => {
            const active = card.dataset.giftValue === selectedValue
            card.classList.toggle("rsvp-contribution-v2__choice-card--active", active)

            const text = card.querySelector(".rsvp-contribution-v2__choice-text")
            if (text) {
                text.classList.toggle("rsvp-contribution-v2__choice-text--muted", !active)
            }

            const indicator = card.querySelector(".rsvp-contribution-v2__choice-indicator")
            if (indicator) {
                indicator.classList.toggle("rsvp-contribution-v2__choice-indicator--empty", !active)

                if (active && !indicator.querySelector(".rsvp-contribution-v2__choice-dot")) {
                    const dot = document.createElement("span")
                    dot.className = "rsvp-contribution-v2__choice-dot"
                    indicator.appendChild(dot)
                }

                if (!active) {
                    indicator.querySelector(".rsvp-contribution-v2__choice-dot")?.remove()
                }
            }
        })
    }

    syncProgressiveBlocks(totalDollars = 0) {
        const give = this.isGivingGift()
        const hasAmount = Number(totalDollars || 0) > 0
        const shouldShowLowerSection = !give || hasAmount || this.usingOther

        if (this.hasAfterAmountBlockTarget) {
            this.afterAmountBlockTarget.classList.toggle("d-none", !shouldShowLowerSection)
        }

        if (this.hasSplitPreviewTarget) {
            this.splitPreviewTarget.classList.toggle("d-none", !give || !hasAmount)
        }

        if (this.hasPayBlockTarget) {
            this.payBlockTarget.classList.toggle("d-none", !give)
        }
    }

    resetAmountSelection() {
        this.selectedAmount = 0
        this.usingOther = false

        if (this.hasAmountInputTarget) {
            this.amountInputTarget.value = ""
        }

        if (this.hasOtherWrapperTarget) {
            this.otherWrapperTarget.classList.add("d-none")

            const otherInput = this.otherWrapperTarget.querySelector('[name="other_amount"]')
            if (otherInput) otherInput.value = ""
        }

        this.clearChipHighlights()

        if (this.hasFeeCopyTarget) {
            this.feeCopyTarget.textContent = ""
        }

        if (this.hasTotalCopyTarget) {
            this.totalCopyTarget.textContent = ""
        }

        if (this.hasReceiptNoteTarget) {
            this.receiptNoteTarget.classList.add("d-none")
        }

        if (this.hasReceiptAmtTarget) {
            this.receiptAmtTarget.textContent = this.moneyCents(0)
        }
    }

    chooseAmount(event) {
        const btn = event.currentTarget
        const amt = parseInt(btn.dataset.amount, 10)
        if (!Number.isFinite(amt)) return

        this.usingOther = false
        this.amountTouched = true

        if (this.hasOtherWrapperTarget) {
            this.otherWrapperTarget.classList.add("d-none")

            const otherInput = this.otherWrapperTarget.querySelector('[name="other_amount"]')
            if (otherInput) otherInput.value = ""
        }

        this.highlightChip(amt)

        this.amountInputTarget.value = String(amt)
        this.selectedAmount = amt

        this.clearAmountError()
        this.updateAll(amt)

        this.formTarget?.dispatchEvent(new CustomEvent("rsvp:amount-changed", { bubbles: true }))
    }

    chooseOther(event) {
        this.usingOther = true
        this.amountTouched = true

        this.highlightOtherChip(event?.currentTarget)

        if (!this.hasOtherWrapperTarget) return

        this.otherWrapperTarget.classList.remove("d-none")

        const input = this.otherWrapperTarget.querySelector('[name="other_amount"]')

        if (input) {
            const rawValue = String(input.value || "").trim()
            const value = parseInt(rawValue || "0", 10) || 0

            if (value >= 10) {
                this.amountInputTarget.value = String(value)
                this.selectedAmount = value
                this.updateAll(value)
                this.clearAmountError()
            } else {
                this.amountInputTarget.value = ""
                this.selectedAmount = 0
                this.updateAll(0)

                if (rawValue.length > 0) {
                    this.showAmountErrorAfterValidation("Please enter an amount of at least $10.")
                } else {
                    this.showAmountErrorAfterValidation("Please select or enter an amount (minimum $10)")
                }
            }

            input.focus()
        }

        this.syncProgressiveBlocks(parseInt(this.amountInputTarget.value || "0", 10) || 0)

        this.formTarget?.dispatchEvent(new CustomEvent("rsvp:amount-changed", {
            bubbles: true,
            detail: {
                source: "rsvp-payment-form",
                usingOther: true,
                amount: this.selectedAmount
            }
        }))

        this.validateAmountSelection({ showError: true, deferError: true })
    }

    onOtherAmount(event) {
        this.usingOther = true
        this.amountTouched = true
        this.highlightOtherChip()

        const rawValue = String(event.currentTarget.value || "").trim()
        const value = parseInt(rawValue || "0", 10) || 0

        if (value >= 10) {
            this.amountInputTarget.value = String(value)
            this.selectedAmount = value
            this.updateAll(value)
            this.clearAmountError()
        } else {
            this.amountInputTarget.value = ""
            this.selectedAmount = 0
            this.updateAll(0)
        }

        this.formTarget?.dispatchEvent(new CustomEvent("rsvp:amount-changed", {
            bubbles: true,
            detail: {
                source: "rsvp-payment-form",
                usingOther: true,
                amount: this.selectedAmount,
                rawOtherAmount: rawValue
            }
        }))

        this.validateAmountSelection({ showError: true, deferError: true })
    }

    updateAll(totalDollars) {
        this.updateSplit(totalDollars)

        if (this.isGivingGift() && totalDollars > 0) {
            const feeCents = this.computeFeeCents(totalDollars)
            const totalCents = totalDollars * 100 + feeCents
            const feeText = this.feeCopyText(totalDollars, feeCents)

            if (this.hasFeeCopyTarget) this.feeCopyTarget.textContent = feeText
            if (this.hasTotalCopyTarget) this.totalCopyTarget.textContent = `Total charged: ${this.moneyCents(totalCents)}`
        } else {
            if (this.hasFeeCopyTarget) this.feeCopyTarget.textContent = ""
            if (this.hasTotalCopyTarget) this.totalCopyTarget.textContent = ""
        }

        this.syncProgressiveBlocks(totalDollars)
    }

    updateSplit(totalDollars) {
        const hs = Number(this.hostSplitValue ?? 0)
        const cs = Number.isFinite(this.charityRateValue) ? this.charityRateValue : (1 - hs)

        const totalCents = Math.round((totalDollars || 0) * 100)
        const charityCents = Math.round(totalCents * cs)
        const giftCents = totalCents - charityCents

        if (this.hasGiftAmtTarget) this.giftAmtTarget.textContent = this.moneyCents(giftCents)
        if (this.hasCharityAmtTarget) this.charityAmtTarget.textContent = this.moneyCents(charityCents)

        const thresholdDollars = Number(this.taxThresholdValue ?? 20)
        const showReceipt = totalCents > 0 && (charityCents / 100) >= thresholdDollars

        if (this.hasReceiptNoteTarget) this.receiptNoteTarget.classList.toggle("d-none", !showReceipt)
        if (this.hasReceiptAmtTarget) this.receiptAmtTarget.textContent = this.moneyCents(charityCents)

        this.updateTaxToggleState({ eligible: showReceipt })
    }

    computeFeeCents(amountDollars) {
        const cents = Math.round(amountDollars * 100)
        const low = Number(this.feeLowThresholdValue || 2000)
        const mid = Number(this.feeMidUpperValue || 8000)
        const rateBps = Number(this.feeRateBpsValue || 295)
        const flat = Number(this.feeMidFlatCentsValue || 250)

        if (cents <= low) return Math.round(cents * rateBps / 10000)
        if (cents <= mid) return flat
        return Math.round(cents * rateBps / 10000)
    }

    feeCopyText(amountDollars, feeCents) {
        const cents = Math.round((amountDollars || 0) * 100)
        const low = Number(this.feeLowThresholdValue || 2000)
        const mid = Number(this.feeMidUpperValue || 8000)
        const ratePct = (Number(this.feeRateBpsValue || 295) / 100).toFixed(2)

        if (cents <= low || cents > mid) {
            return `A service fee of ${ratePct}% will be added to your contribution (${this.moneyCents(feeCents)}).`
        }

        const flat = Number(this.feeMidFlatCentsValue || 250)
        return `A service fee of ${this.moneyCents(flat)} will be added to your contribution.`
    }

    updateTaxToggleState({ eligible }) {
        if (this.hasTaxReceiptChoiceTarget) {
            this.taxReceiptChoiceTarget.classList.toggle("d-none", !eligible)
        }

        if (!this.hasTaxToggleTarget) return

        const box = this.taxToggleTarget

        if (!eligible) {
            box.checked = false
            box.disabled = true
            box.setAttribute("aria-disabled", "true")
            box.setAttribute("title", "Tax receipts are available when the charity share is at least $20.")

            if (this.hasTaxBlockTarget) {
                this.taxBlockTarget.classList.add("d-none")
            }
        } else {
            box.disabled = false
            box.removeAttribute("aria-disabled")
            box.removeAttribute("title")
        }
    }

    moneyCents(cents) {
        const sym = this.currencySymbolValue || "$"
        const v = Math.max(0, Math.round(cents || 0)) / 100

        return `${sym}${v.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`
    }

    highlightChip(value) {
        this.clearChipHighlights()

        if (!this.hasChipsTarget) return

        const btn = this.chipsTarget.querySelector(`[data-amount="${value}"]`)
        btn?.classList.add("active")
    }

    highlightOtherChip(button = null) {
        this.clearChipHighlights()

        if (button) {
            button.classList.add("active")
            return
        }

        if (!this.hasChipsTarget) return

        const other = this.chipsTarget.querySelector(".amount-chip--other")
        other?.classList.add("active")
    }

    clearChipHighlights() {
        if (!this.hasChipsTarget) return

        this.chipsTarget
            .querySelectorAll(".amount-chip.active")
            .forEach((button) => button.classList.remove("active"))
    }
}
